import {
  DATA_FONT,
  FIB_TAG_BG,
  FIBONACCI_DEFAULT_LEVELS,
} from "@/lib/chart-drawings/defaults";
import {
  cycleTimeStamps,
  extendLineThroughPoints,
  fibPriceLevels,
  gannFanRayEndpoints,
  lineAtAngleFromAnchor,
  parallelLineThroughPoint,
  segmentBetween,
  spanX,
  visibleFibLevels,
} from "../drawing-math";
import {
  chipFigure,
  fibBandFigures,
  horizontalLineFigure,
  lineFigure,
  readOverlaySettings,
  resolveChipColor,
  textLabelFigure,
} from "../overlay-figures";
import { safeRegisterOverlay } from "./kline";

type Coord = { x: number; y: number };

type OverlayFigure =
  | ReturnType<typeof lineFigure>
  | ReturnType<typeof horizontalLineFigure>
  | ReturnType<typeof textLabelFigure>
  | Record<string, unknown>;

/** Ratios that anchor the structure — drawn heavier than the interior levels. */
const FIB_ANCHOR_LEVELS = new Set([0, 0.5, 1]);

function fibLineStyle(
  style: Record<string, unknown>,
  level: number,
  anchorEmphasis: boolean
): Record<string, unknown> {
  if (!anchorEmphasis || !FIB_ANCHOR_LEVELS.has(level)) return style;
  return { ...style, lineWidth: ((style.lineWidth as number) || 1) + 1 };
}

function fibRatioText(level: number): string {
  return `${(level * 100).toFixed(1)}%`;
}

function fibTagText(level: number, price: number | undefined, showPrices: boolean): string {
  if (!showPrices || price === undefined || !Number.isFinite(price)) {
    return fibRatioText(level);
  }
  const abs = Math.abs(price);
  const digits = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
  return `${fibRatioText(level)}  ${price.toFixed(digits)}`;
}

/**
 * A level tag: ratio + the price that ratio actually sits on.
 *
 * Normally right-anchored just past the study's end. When that would run under
 * the price axis, the tag flips inside the study instead of being clipped.
 */
function fibTagFigures(
  x: number,
  y: number,
  level: number,
  price: number | undefined,
  style: Record<string, unknown>,
  showPrices: boolean,
  bounding?: { width?: number }
) {
  const color = (style.lineColor as string) || resolveChipColor(style);
  const text = fibTagText(level, price, showPrices);
  const fontSize = (style.fontSize as number) || 10;
  const estWidth = text.length * fontSize * 0.62 + 12;
  const paneWidth = bounding?.width;
  const flip = typeof paneWidth === "number" && x + estWidth > paneWidth;
  const tagX = flip ? x - 8 : x;
  // A dark tag with a coloured border + caption, NOT a saturated fill: five of
  // these stacked on one chart must read as quiet structure, not as five badges.
  return chipFigure(tagX, y, text, style, {
    align: flip ? "right" : "left",
    fontSize,
    fontFamily: (style.fontFamily as string) || DATA_FONT,
    paddingX: 4,
    paddingY: 2,
    borderRadius: 3,
    color: FIB_TAG_BG,
    borderColor: color,
    textColor: color,
  });
}

/**
 * The x-range a Fibonacci study occupies.
 *
 * Studies are clipped to their OWN anchors on the time axis — they start where
 * the user clicked and stop where they released. `extendRight` opts a drawing
 * into projecting forward to the right edge of the pane.
 */
function fibSpan(coords: Coord[], extendRight: boolean, bounding?: { width?: number }) {
  return spanX(coords, extendRight ? (bounding?.width ?? undefined) : undefined);
}

function connectPoints(coords: Coord[], style: Record<string, unknown>) {
  const figures: OverlayFigure[] = [];
  for (let i = 0; i < coords.length - 1; i++) {
    figures.push(
      lineFigure(
        [
          { x: coords[i].x, y: coords[i].y },
          { x: coords[i + 1].x, y: coords[i + 1].y },
        ],
        style
      )
    );
  }
  return figures;
}

export function registerNewDrawingOverlays() {
  registerTrendByAngle();
  registerStdDevChannel();
  registerRegressionChannel();
  registerAndrewsPitchfork();
  registerFibonacciOverlays();
  registerGannLine();
  registerGannFan();
  registerSignalArrows();
  registerCycleLines();
  registerTextLabel();
  registerElliottCorrectiveWave();
}

function registerTrendByAngle() {
  safeRegisterOverlay({
    name: "customTrendByAngle",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, angle } = readOverlaySettings(overlay);
      const p0 = coordinates[0];
      const p1 = coordinates[1];
      const len = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      const [a, b] = lineAtAngleFromAnchor(p0, len, angle);
      return [lineFigure([a, b], style)];
    },
  });
}

function registerStdDevChannel() {
  safeRegisterOverlay({
    name: "customStdDevChannel",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, computed } = readOverlaySettings(overlay);
      const figures = [];
      const x1 = coordinates[0].x;
      const x2 = coordinates[1].x;
      const y1 = coordinates[0].y;
      const y2 = coordinates[1].y;
      const [mainA, mainB] = extendLineThroughPoints(x1, y1, x2, y2);
      figures.push(lineFigure([mainA, mainB], style));

      const ovl = overlay as {
        points?: Array<{ value?: number }>;
        extendData?: { settings?: Record<string, unknown> };
      };
      const p0v = ovl.points?.[0]?.value;
      const p1v = ovl.points?.[1]?.value;
      if (
        computed &&
        typeof computed.stdDev === "number" &&
        typeof p0v === "number" &&
        typeof p1v === "number"
      ) {
        const devMult =
          typeof computed.deviations === "number" ? computed.deviations : 2;
        const priceRange = Math.abs(p1v - p0v) || 1;
        const pixelRange = Math.abs(coordinates[1].y - coordinates[0].y) || 1;
        const offset = ((devMult * (computed.stdDev as number)) / priceRange) * pixelRange;
        figures.push(
          lineFigure(
            [
              { x: mainA.x, y: mainA.y - offset },
              { x: mainB.x, y: mainB.y - offset },
            ],
            style
          )
        );
        figures.push(
          lineFigure(
            [
              { x: mainA.x, y: mainA.y + offset },
              { x: mainB.x, y: mainB.y + offset },
            ],
            style
          )
        );
      }
      return figures;
    },
  });
}

function registerRegressionChannel() {
  safeRegisterOverlay({
    name: "customRegressionChannel",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, computed } = readOverlaySettings(overlay);
      const x1 = coordinates[0].x;
      const y1 = coordinates[0].y;
      const x2 = coordinates[1].x;
      const y2 = coordinates[1].y;

      const ovl = overlay as {
        points?: Array<{ timestamp?: number; value?: number }>;
      };
      const t0 = ovl.points?.[0]?.timestamp;
      const t1 = ovl.points?.[1]?.timestamp;
      const p0v = ovl.points?.[0]?.value;
      const p1v = ovl.points?.[1]?.value;

      // MT-style regression channel: the main line is the least-squares fit over
      // the anchored range, with parallel bands at ±(deviations · stdDev). We map
      // price → pixel using the two anchor points as a linear reference scale.
      if (
        computed &&
        typeof computed.slope === "number" &&
        typeof computed.intercept === "number" &&
        typeof computed.stdDev === "number" &&
        typeof t0 === "number" &&
        typeof t1 === "number" &&
        typeof p0v === "number" &&
        typeof p1v === "number" &&
        p1v !== p0v
      ) {
        const slope = computed.slope as number;
        const intercept = computed.intercept as number;
        const stdDev = computed.stdDev as number;
        const devMult =
          typeof computed.deviations === "number"
            ? (computed.deviations as number)
            : 2;

        // Linear price → pixel-y scale from the two anchors.
        const priceToY = (price: number) =>
          y1 + ((price - p0v) / (p1v - p0v)) * (y2 - y1);

        // Regression line prices at the two anchor timestamps.
        const regP0 = slope * t0 + intercept;
        const regP1 = slope * t1 + intercept;
        const ry0 = priceToY(regP0);
        const ry1 = priceToY(regP1);

        const [mainA, mainB] = extendLineThroughPoints(x1, ry0, x2, ry1);
        const figures = [lineFigure([mainA, mainB], style)];

        const bandPx =
          Math.abs((devMult * stdDev) / (p1v - p0v)) * Math.abs(y2 - y1);
        figures.push(
          lineFigure(
            [
              { x: mainA.x, y: mainA.y - bandPx },
              { x: mainB.x, y: mainB.y - bandPx },
            ],
            style
          )
        );
        figures.push(
          lineFigure(
            [
              { x: mainA.x, y: mainA.y + bandPx },
              { x: mainB.x, y: mainB.y + bandPx },
            ],
            style
          )
        );
        return figures;
      }

      // Fallback (regression not yet computed): draw the anchor line only.
      const [mainA, mainB] = extendLineThroughPoints(x1, y1, x2, y2);
      return [lineFigure([mainA, mainB], style)];
    },
  });
}

function registerAndrewsPitchfork() {
  safeRegisterOverlay({
    name: "customAndrewsPitchfork",
    totalStep: 4,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }: { coordinates: Coord[]; overlay?: unknown }) => {
      if (coordinates.length < 3) {
        return coordinates.length >= 2 ? connectPoints(coordinates, readOverlaySettings(overlay as { extendData?: { settings?: Record<string, unknown> } }).style) : [];
      }
      const { style } = readOverlaySettings(overlay as { extendData?: { settings?: Record<string, unknown> } });
      const p0 = coordinates[0];
      const p1 = coordinates[1];
      const p2 = coordinates[2];
      const mx = (p0.x + p2.x) / 2;
      const my = (p0.y + p2.y) / 2;
      const figures = [];
      figures.push(lineFigure(extendLineThroughPoints(p1.x, p1.y, mx, my), style));
      figures.push(lineFigure(extendLineThroughPoints(p0.x, p0.y, p0.x + (mx - p1.x), p0.y + (my - p1.y)), style));
      figures.push(lineFigure(extendLineThroughPoints(p2.x, p2.y, p2.x + (mx - p1.x), p2.y + (my - p1.y)), style));
      return figures;
    },
  });
}

function registerFibonacciOverlays() {
  registerFibonacciRetracement();

  safeRegisterOverlay({
    name: "customFibonacciTimeZones",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, levels, anchorEmphasis } = readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const x0 = coordinates[0].x;
      const x1 = coordinates[1].x;
      const span = x1 - x0;
      // Verticals span the visible pane height only — never -h..2h.
      const h = bounding?.height ?? 1000;
      const figures: OverlayFigure[] = [];
      for (const l of lv) {
        const x = x0 + span * l.value;
        figures.push(
          lineFigure(
            [
              { x, y: 0 },
              { x, y: h },
            ],
            fibLineStyle(style, l.value, anchorEmphasis),
            "lineColor"
          )
        );
        figures.push(
          ...fibTagFigures(x + 3, 14, l.value, undefined, style, false)
        );
      }
      return figures;
    },
  });

  safeRegisterOverlay({
    name: "customFibonacciFan",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, levels, extendRight, anchorEmphasis } = readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const origin = coordinates[0];
      const corner = coordinates[1];
      const priceDiff = corner.y - origin.y;
      // Rays are clipped to the fan's own [t0,t1], not run out to EXTENSION_FACTOR.
      const { minX, maxX } = fibSpan(coordinates, extendRight, bounding);
      const figures: OverlayFigure[] = [];
      for (const l of lv) {
        const endY = origin.y + priceDiff * l.value;
        const [a, b] = segmentBetween(origin.x, origin.y, corner.x, endY, minX, maxX);
        figures.push(
          lineFigure([a, b], fibLineStyle(style, l.value, anchorEmphasis), "lineColor")
        );
        // Fan rays previously carried no label at all. A ray has no single price,
        // so the tag shows the ratio only, parked at the ray's right-hand tip.
        figures.push(...fibTagFigures(b.x + 4, b.y, l.value, undefined, style, false, bounding));
      }
      return figures;
    },
  });

  safeRegisterOverlay({
    name: "customFibonacciArcs",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 2) return [];
      const { style, levels, anchorEmphasis } = readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const x0 = coordinates[0].x;
      const y0 = coordinates[0].y;
      const x1 = coordinates[1].x;
      const y1 = coordinates[1].y;
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      const maxR = Math.hypot(x1 - x0, y1 - y0) / 2;
      const figures: OverlayFigure[] = [];
      for (const l of lv) {
        const r = maxR * l.value;
        if (r <= 0) continue;
        const ls = fibLineStyle(style, l.value, anchorEmphasis);
        const segments = 32;
        const pts: Coord[] = [];
        for (let i = 0; i <= segments; i++) {
          const t = Math.PI * (i / segments);
          pts.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
        }
        for (let i = 0; i < pts.length - 1; i++) {
          figures.push(lineFigure([pts[i], pts[i + 1]], ls, "lineColor"));
        }
        // Arcs previously carried no label at all — tag the arc apex.
        figures.push(...fibTagFigures(cx + 4, cy + r, l.value, undefined, style, false, bounding));
      }
      return figures;
    },
    drawExtend: () => undefined,
  });

  safeRegisterOverlay({
    name: "customFibonacciChannel",
    totalStep: 4,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 3) {
        return coordinates.length >= 2
          ? connectPoints(coordinates, readOverlaySettings(overlay).style)
          : [];
      }
      const { settings, style, levels, extendRight, showBands, showPrices, anchorEmphasis } =
        readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const ax = coordinates[0].x;
      const ay = coordinates[0].y;
      const bx = coordinates[1].x;
      const by = coordinates[1].y;
      const cx = coordinates[2].x;
      const cy = coordinates[2].y;
      // Both rails are clipped to the channel's own span on the time axis.
      const { minX, maxX } = fibSpan(coordinates, extendRight, bounding);
      const [d1, d2] = parallelLineThroughPoint(ax, ay, bx, by, cx, cy, { minX, maxX });
      const figures: OverlayFigure[] = [
        lineFigure(segmentBetween(ax, ay, bx, by, minX, maxX), style, "lineColor"),
        lineFigure([d1, d2], style, "lineColor"),
      ];

      const rows = lv.map((l) => ({ level: l.value, y: ay + (cy - ay) * l.value }));
      // Real prices come from the anchor points, not from pixel positions.
      const pts = settings.points as Array<{ value?: number }> | undefined;
      const prices = fibPriceLevels(pts?.[0]?.value ?? NaN, pts?.[2]?.value ?? NaN, lv);

      if (showBands) {
        figures.push(
          ...fibBandFigures(rows, minX, maxX, (style.lineColor as string) || undefined)
        );
      }
      rows.forEach((row, i) => {
        figures.push(
          horizontalLineFigure(
            row.y,
            minX,
            maxX,
            fibLineStyle(style, row.level, anchorEmphasis),
            "lineColor"
          )
        );
        figures.push(
          ...fibTagFigures(maxX + 4, row.y, row.level, prices[i]?.price, style, showPrices, bounding)
        );
      });
      return figures;
    },
  });

  safeRegisterOverlay({
    name: "customFibonacciExpansion",
    totalStep: 4,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 3) {
        return coordinates.length >= 2
          ? connectPoints(coordinates, readOverlaySettings(overlay).style)
          : [];
      }
      const { settings, style, levels, extendRight, showBands, showPrices, anchorEmphasis } =
        readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const a = coordinates[0];
      const b = coordinates[1];
      const c = coordinates[2];
      const figures: OverlayFigure[] = connectPoints([a, b, c], style);
      const abY = b.y - a.y;
      // Was `maxX + 200` — an arbitrary overshoot unrelated to the drawing.
      const { minX, maxX } = fibSpan(coordinates, extendRight, bounding);

      const pts = settings.points as Array<{ value?: number }> | undefined;
      const p0 = pts?.[0]?.value;
      const p1 = pts?.[1]?.value;
      const p2 = pts?.[2]?.value;
      const rows = lv.map((l) => ({ level: l.value, y: c.y + abY * l.value }));

      if (showBands) {
        figures.push(
          ...fibBandFigures(rows, minX, maxX, (style.lineColor as string) || undefined)
        );
      }
      rows.forEach((row) => {
        const price =
          p0 !== undefined && p1 !== undefined && p2 !== undefined
            ? p2 + (p1 - p0) * row.level
            : undefined;
        figures.push(
          horizontalLineFigure(
            row.y,
            minX,
            maxX,
            fibLineStyle(style, row.level, anchorEmphasis),
            "lineColor"
          )
        );
        figures.push(
          ...fibTagFigures(maxX + 4, row.y, row.level, price, style, showPrices, bounding)
        );
      });
      return figures;
    },
  });
}

/**
 * Fibonacci Retracement.
 *
 * Previously this rode KLineCharts' built-in `fibonacciLine` overlay, which spans
 * the whole pane by design and offers no bands, no price tags and no per-level
 * emphasis. It now shares the bounded/banded/tagged path with its four siblings.
 */
function registerFibonacciRetracement() {
  safeRegisterOverlay({
    name: "customFibonacciRetracement",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
      bounding?: { width?: number; height?: number };
    }) => {
      if (coordinates.length < 2) return [];
      const { settings, style, levels, extendRight, showBands, showPrices, anchorEmphasis } =
        readOverlaySettings(overlay);
      const lv = visibleFibLevels(levels, FIBONACCI_DEFAULT_LEVELS);
      const y0 = coordinates[0].y;
      const y1 = coordinates[1].y;
      // The study starts and stops where the user anchored it.
      const { minX, maxX } = fibSpan(coordinates, extendRight, bounding);

      const pts = settings.points as Array<{ value?: number }> | undefined;
      const prices = fibPriceLevels(pts?.[0]?.value ?? NaN, pts?.[1]?.value ?? NaN, lv);
      const rows = lv.map((l) => ({ level: l.value, y: y0 + (y1 - y0) * l.value }));

      const figures: OverlayFigure[] = [];
      if (showBands) {
        figures.push(
          ...fibBandFigures(rows, minX, maxX, (style.lineColor as string) || undefined)
        );
      }
      rows.forEach((row, i) => {
        figures.push(
          horizontalLineFigure(
            row.y,
            minX,
            maxX,
            fibLineStyle(style, row.level, anchorEmphasis),
            "lineColor"
          )
        );
        figures.push(
          ...fibTagFigures(maxX + 4, row.y, row.level, prices[i]?.price, style, showPrices, bounding)
        );
      });
      return figures;
    },
  });
}

function registerGannLine() {
  safeRegisterOverlay({
    name: "customGannLine",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }: { coordinates: Coord[]; overlay?: unknown }) => {
      if (coordinates.length < 2) return [];
      const { style } = readOverlaySettings(overlay as { extendData?: { settings?: Record<string, unknown> } });
      const [a, b] = extendLineThroughPoints(
        coordinates[0].x,
        coordinates[0].y,
        coordinates[1].x,
        coordinates[1].y
      );
      return [lineFigure([a, b], style)];
    },
  });
}

function registerGannFan() {
  safeRegisterOverlay({
    name: "customGannFan",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }: { coordinates: Coord[]; overlay?: unknown }) => {
      if (coordinates.length < 2) return [];
      const { style, divisions } = readOverlaySettings(
        overlay as { extendData?: { settings?: Record<string, unknown> } }
      );
      const rays = gannFanRayEndpoints(coordinates[0], coordinates[1], divisions);
      return rays.map(([a, b]) => lineFigure([a, b], style));
    },
  });
}

function registerSignalArrows() {
  const makeSignal = (name: string, direction: "up" | "down") => {
    safeRegisterOverlay({
      name,
      totalStep: 2,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      createPointFigures: ({ coordinates, overlay }: { coordinates: Coord[]; overlay?: unknown }) => {
        if (coordinates.length < 1) return [];
        const { style } = readOverlaySettings(
          overlay as { extendData?: { settings?: Record<string, unknown> } }
        );
        const color = (style.color as string) || "#26a69a";
        const p = coordinates[0];
        const w = 8;
        const h = 12;
        const isUp = direction === "up";
        const tip = { x: p.x, y: p.y };
        const left = { x: p.x - w, y: p.y + (isUp ? h : -h) };
        const right = { x: p.x + w, y: p.y + (isUp ? h : -h) };
        return [
          {
            type: "polygon",
            attrs: { coordinates: [tip, left, right] },
            styles: { style: "fill", color },
          },
        ];
      },
    });
  };
  makeSignal("customSignalArrowUp", "up");
  makeSignal("customSignalArrowDown", "down");
}

function registerCycleLines() {
  safeRegisterOverlay({
    name: "customCycleLines",
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
    }: {
      coordinates: Coord[];
      overlay?: unknown;
      bounding?: { height?: number };
    }) => {
      if (coordinates.length < 2) return [];
      const { style } = readOverlaySettings(
        overlay as { extendData?: { settings?: Record<string, unknown> } }
      );
      const x0 = coordinates[0].x;
      const x1 = coordinates[1].x;
      const period = x1 - x0;
      if (period === 0) return [];
      const h = bounding?.height ?? 2000;
      const figures = [];
      const count = 50;
      for (let k = 0; k < count; k++) {
        const x = x0 + k * period;
        figures.push(
          lineFigure(
            [
              { x, y: -h },
              { x, y: h * 2 },
            ],
            style
          )
        );
      }
      return figures;
    },
  });
}

function registerTextLabel() {
  safeRegisterOverlay({
    name: "customTextLabel",
    totalStep: 2,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({
      coordinates,
      overlay,
    }: {
      coordinates: Coord[];
      overlay?: { extendData?: { settings?: Record<string, unknown> } };
    }) => {
      if (coordinates.length < 1) return [];
      const { settings, style } = readOverlaySettings(overlay);
      const text = (settings.text as string) || "Label";
      // The whole chip takes the chosen colour — fill, border and auto-inked caption.
      return chipFigure(coordinates[0].x, coordinates[0].y, text, style, {
        align: "center",
        paddingX: 7,
        paddingY: 4,
        borderRadius: 5,
      });
    },
  });
}

function registerElliottCorrectiveWave() {
  safeRegisterOverlay({
    name: "customElliottCorrectiveWave",
    totalStep: 4,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    createPointFigures: ({ coordinates, overlay }: { coordinates: Coord[]; overlay?: unknown }) => {
      const { style } = readOverlaySettings(
        overlay as { extendData?: { settings?: Record<string, unknown> } }
      );
      const figures = connectPoints(coordinates, style);
      const labels = ["A", "B", "C"];
      for (let i = 0; i < coordinates.length && i < 3; i++) {
        const offset = (style.labelOffset as number) ?? 15;
        const yOffset = i % 2 === 0 ? -offset : offset;
        // A text figure cannot paint a background — this needs a real chip.
        figures.push(
          ...chipFigure(
            coordinates[i].x,
            coordinates[i].y + yOffset,
            labels[i],
            style,
            {
              align: "center",
              fontSize: (style.labelSize as number) || 11,
              fontWeight: "bold",
              paddingX: 5,
              paddingY: 2,
              borderRadius: 3,
            }
          )
        );
      }
      return figures;
    },
  });
}
